import { useState, useEffect, useCallback } from 'react';
import { PrimeArtifactService } from '../services/primeArtifactService';

interface TabValidation {
  is_complete: boolean;
  is_approved: boolean;
  issues: string[];
  warnings?: string[];
  status: 'incomplete' | 'needs_approval' | 'approved';
  [key: string]: any;
}

interface TabValidationStatus {
  details: TabValidation;
  states: TabValidation;
  transitions: TabValidation;
  processes: TabValidation;
}

export const useTabValidation = (artifactId: string | null | undefined) => {
  const [validation, setValidation] = useState<TabValidationStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchValidation = useCallback(async () => {
    if (!artifactId) {
      setValidation(null);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await PrimeArtifactService.getTabValidation(artifactId);
      setValidation(result);
    } catch (err) {
      console.error('Error fetching tab validation:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch validation');
    } finally {
      setLoading(false);
    }
  }, [artifactId]);

  useEffect(() => {
    fetchValidation();
  }, [fetchValidation]);

  const approveDetails = useCallback(async () => {
    if (!artifactId) return;
    
    try {
      await PrimeArtifactService.approveDetails(artifactId);
      await fetchValidation();
    } catch (err) {
      console.error('Error approving details:', err);
      throw err;
    }
  }, [artifactId, fetchValidation]);

  const approveStates = useCallback(async () => {
    if (!artifactId) return;
    
    try {
      await PrimeArtifactService.approveStates(artifactId);
      await fetchValidation();
    } catch (err) {
      console.error('Error approving states:', err);
      throw err;
    }
  }, [artifactId, fetchValidation]);

  const approveTransitions = useCallback(async () => {
    if (!artifactId) return;
    
    try {
      await PrimeArtifactService.approveTransitions(artifactId);
      await fetchValidation();
    } catch (err) {
      console.error('Error approving transitions:', err);
      throw err;
    }
  }, [artifactId, fetchValidation]);

  const approveProcesses = useCallback(async () => {
    if (!artifactId) return;
    
    try {
      await PrimeArtifactService.approveProcesses(artifactId);
      await fetchValidation();
    } catch (err) {
      console.error('Error approving processes:', err);
      throw err;
    }
  }, [artifactId, fetchValidation]);

  return {
    validation,
    loading,
    error,
    fetchValidation,
    approveDetails,
    approveStates,
    approveTransitions,
    approveProcesses
  };
};

